class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');

    if (button) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }

    setTimeout(() => window.dispatchEvent(new Event('resize.resize-select')), 10);
  }
}

const PLP_BODY_VIEWS = ['view-model', 'view-product', 'view-men', 'view-women'];
const activeFacetsSelectors = ['#Facet-Drawer .active-facets', '.facets--bar .active-facets'];
const MAX_FILTER_CACHE_ENTRIES = 30;

const isSearchPage = () => document.body.classList.contains('template-search');

const getPriceRangeStorageKey = () => {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = (params.get('q') || '').toLowerCase();
  return `price-range:${window.location.pathname}:${searchTerm}`;
};

const getStoredPriceRange = () => {
  try {
    const rawValue = window.sessionStorage.getItem(getPriceRangeStorageKey());
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    const min = parsePrice(parsed?.min);
    const max = parsePrice(parsed?.max);
    if (min == null || max == null) return null;
    return { min, max };
  } catch (error) {
    return null;
  }
};

const setStoredPriceRange = (min, max) => {
  if (min == null || max == null) return;
  try {
    window.sessionStorage.setItem(getPriceRangeStorageKey(), JSON.stringify({ min, max }));
  } catch (error) {
    // Ignore storage failures (private mode/quota).
  }
};

const getBasePriceRangeFetchQuery = () => {
  const params = new URLSearchParams(window.location.search);
  [...params.keys()].forEach((paramName) => {
    if (paramName.startsWith('filter.')) params.delete(paramName);
  });
  params.delete('page');
  params.set('sort_by', 'price-ascending');
  return params.toString();
};

const fetchBasePriceRange = async () => {
  const sectionId = document.getElementById('product-grid')?.dataset.id;
  if (!sectionId) return null;

  const query = getBasePriceRangeFetchQuery();
  const url = `${window.location.pathname}?section_id=${sectionId}${query ? `&${query}` : ''}`;

  try {
    const response = await fetch(url);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const minCard = parsed.querySelector('product-card[data-price-min]');
    const slider = parsed.querySelector('.price_slider');

    const minCents = minCard ? parseFloat(minCard.getAttribute('data-price-min')) : null;
    const min = minCents == null || Number.isNaN(minCents) ? null : minCents / 100;
    const max = parsePrice(slider?.dataset?.max);

    if (min == null || max == null) return null;
    return { min, max };
  } catch (error) {
    return null;
  }
};

const getFilterParams = (searchParams) => {
  const params = new Map();
  for (const [key, value] of new URLSearchParams(searchParams).entries()) {
    if (!key.startsWith('filter.') && key !== 'sort_by') continue;
    if (!params.has(key)) params.set(key, new Set());
    params.get(key).add(value);
  }
  return params;
};

const getFilterLabel = (paramKey) => {
  if (paramKey === 'sort_by') return 'Sort';
  if (paramKey.includes('price')) return 'Price';
  const field = document.querySelector(`input[name="${CSS.escape(paramKey)}"], select[name="${CSS.escape(paramKey)}"]`);
  const title = field?.closest('collapsible-row, .thb-filter, .thb-filter-sort')?.querySelector('.thb-filter-title');
  return title?.textContent.trim() || paramKey.split('.').pop() || paramKey;
};

const getFilterValueLabel = (paramKey, paramValue, prevParams) => {
  if (paramKey.includes('price')) return String(paramValue);
  if (paramKey === 'sort_by') {
    return document.querySelector(`select[name="sort_by"] option[value="${CSS.escape(paramValue)}"]`)?.textContent.trim() || String(paramValue);
  }

  for (const input of document.querySelectorAll(`input[name="${CSS.escape(paramKey)}"]`)) {
    if (input.value !== paramValue) continue;
    const label = input.labels?.[0];
    if (!label) continue;
    return label.dataset.tooltip?.replace(/\s*\(\d+\)$/, '') || label.textContent.trim();
  }

  if (!prevParams) return String(paramValue);

  const prevValues = getFilterParams(prevParams).get(paramKey) || new Set();
  for (const chip of document.querySelectorAll('.active-facets .active-facets__button:not(.active-facets__button-remove)')) {
    const chipValues = getFilterParams(new URL(chip.href, window.location.origin).search.slice(1)).get(paramKey) || new Set();
    const removed = [...prevValues].filter((value) => !chipValues.has(value));
    if (removed.length === 1 && removed[0] === paramValue) {
      return chip.firstChild?.textContent?.trim() || chip.textContent.trim();
    }
  }

  return String(paramValue);
};

const pushSearchFilterEvent = (action, paramKey, paramValue, searchTerm, prevParams) => {
  const data = {
    filter_action: action,
    filter_name: getFilterLabel(paramKey),
    filter_value: getFilterValueLabel(paramKey, paramValue, action === 'remove' ? prevParams : null),
    search_term: searchTerm,
    page_type: 'search'
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'search_filter_interaction', ...data });
  if (typeof window.gtag === 'function') window.gtag('event', 'search_filter_interaction', data);
};

const trackSearchFilterChanges = (prevParams, newParams) => {
  if (!isSearchPage()) return;

  const prev = getFilterParams(prevParams);
  const next = getFilterParams(newParams);
  const searchTerm = new URLSearchParams(newParams).get('q') || '';

  for (const paramKey of new Set([...prev.keys(), ...next.keys()])) {
    const prevValues = prev.get(paramKey) || new Set();
    const nextValues = next.get(paramKey) || new Set();

    if (paramKey === 'sort_by' && prevValues.size && nextValues.size) {
      const sortValue = [...nextValues][0];
      if (!prevValues.has(sortValue)) pushSearchFilterEvent('change', paramKey, sortValue, searchTerm);
      continue;
    }

    for (const value of prevValues) {
      if (!nextValues.has(value)) pushSearchFilterEvent('remove', paramKey, value, searchTerm, prevParams);
    }

    for (const value of nextValues) {
      if (prevValues.has(value)) continue;
      const action = !prevValues.size ? 'apply' : paramKey.includes('price') ? 'change' : 'apply';
      pushSearchFilterEvent(action, paramKey, value, searchTerm);
    }
  }
};

const normalizeSearchParams = (searchParams) => {
  const entries = [...new URLSearchParams(searchParams).entries()]
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  return new URLSearchParams(entries).toString();
};

const parsePrice = (value) => {
  if (value === undefined || value === null || value === '') return null;

  const rawValue = String(value).replace(/\s/g, '').replace(/[^\d,.-]/g, '');
  if (!rawValue) return null;

  const lastCommaIndex = rawValue.lastIndexOf(',');
  const lastDotIndex = rawValue.lastIndexOf('.');

  let normalized = rawValue;
  if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
    const decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
    normalized = normalized.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
    normalized = normalized.replace(decimalSeparator, '.');
  } else if (lastCommaIndex !== -1) {
    normalized = normalized.replace(/\./g, '');
    const pieces = rawValue.split(',');
    if (pieces.length > 2) {
      const decimalPart = pieces.pop();
      normalized = `${pieces.join('')}.${decimalPart}`;
    } else if (pieces.length === 2 && pieces[1].length === 3 && pieces[0].length <= 3) {
      normalized = pieces.join('');
    } else {
      normalized = normalized.replace(',', '.');
    }
  } else if (lastDotIndex !== -1) {
    const pieces = rawValue.split('.');
    if (pieces.length > 2) {
      const decimalPart = pieces.pop();
      normalized = `${pieces.join('')}.${decimalPart}`;
    } else if (pieces.length === 2 && pieces[1].length === 3 && pieces[0].length <= 3) {
      normalized = pieces.join('');
    }
  }

  const number = Number(normalized);
  return Number.isNaN(number) ? null : number;
};

const formatPriceInput = (value, usesCommaDecimals) => {
  const amount = parsePrice(value);
  if (amount == null) return '';
  const formatted = amount.toFixed(2);
  return usesCommaDecimals ? formatted.replace('.', ',') : formatted;
};

const normalizeSearchTaxonomyParams = (params) => {
  if (!isSearchPage()) return params;

  const normalized = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (key.startsWith('filter.p.t.') && value.includes('__')) {
      normalized.append(key, value.split('__').pop());
      continue;
    }
    normalized.append(key, value);
  }

  return normalized;
};

const isPriceFacetEvent = (event) => event?.target?.closest('[data-index="price"]');

const getPriceParams = () => {
  const params = new URLSearchParams(FacetFiltersForm?.searchParamsPrev || window.location.search.slice(1));
  return {
    min: params.get('filter.v.price.gte'),
    max: params.get('filter.v.price.lte')
  };
};

const updateResultsCount = (count) => {
  document.querySelectorAll('.thb-filter-count .facets__label').forEach((label) => {
    label.dataset.count = String(count);
  });
  const searchLabel = document.querySelector('.search-label p');
  if (searchLabel && count !== '') {
    if (!searchLabel.dataset.countTemplate) {
      const hasNumber = /\d[\d,.]*/.test(searchLabel.textContent);
      searchLabel.dataset.countTemplate = hasNumber
        ? searchLabel.textContent.replace(/\d[\d,.]*/, '__COUNT__')
        : `${searchLabel.textContent} __COUNT__`;
    }
    searchLabel.textContent = searchLabel.dataset.countTemplate.replace('__COUNT__', count);
  }
};


const applyClientSidePriceFilter = (minOverride, maxOverride) => {
  const { min, max } = (minOverride !== undefined || maxOverride !== undefined)
    ? { min: minOverride ?? null, max: maxOverride ?? null }
    : getPriceParams();
  const minPrice = parsePrice(min);
  const maxPrice = parsePrice(max);

  document.querySelectorAll('#ProductGridContainer li.column').forEach((li) => {
    const card = li.querySelector('product-card[data-price-min]');
    if (!card) return;

    const cardMin = Number(card.dataset.priceMin) / 100;
    const cardMax = Number(card.dataset.priceMax) / 100;

    const filtered = (minPrice !== null && cardMax < minPrice) || (maxPrice !== null && cardMin > maxPrice);
    li.classList.toggle('is-price-filtered-out', filtered);
  });
};

const finishSectionRender = (html, event) => {
  FacetFiltersForm.renderFilters(html, event);
  FacetFiltersForm.renderProductGridContainer(html, event);
  FacetFiltersForm.renderProductCount(html);
  setTimeout(() => window.dispatchEvent(new Event('resize.resize-select')), 10);
};


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((target) => {
      this.onSubmitHandler({ preventDefault: () => {}, target });
    }, 500);
  }

  // connectedCallback instead of constructor so the form listener attaches even when
  // this element is re-created via innerHTML (sidebar layout AJAX re-renders).
  connectedCallback() {
    this.querySelector('form')?.addEventListener('input', (event) => {
      this.debouncedOnSubmit(event.target);
    });
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const rawParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      const normalizedTaxonomyParams = normalizeSearchTaxonomyParams(new URLSearchParams(rawParams));
      const searchParams = normalizeSearchParams(normalizedTaxonomyParams.toString());
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });

    const productGrid = document.getElementById('ProductGridContainer');
    if (!productGrid) return;

    productGrid.addEventListener('lazyloaded', (event) => {
      if (event.target.closest('.product-card-slider')) {
        FacetFiltersForm.resizeProductCardSliders(productGrid);
      }
    }, true);

    if (!FacetFiltersForm.hasMobileApplyClickListener) {
      document.addEventListener('click', (event) => {
        const applyButton = event.target.closest('.mobile-filters-apply');
        if (!applyButton) return;
        event.preventDefault();
        document.querySelector('.click-capture')?.click();
      });
      FacetFiltersForm.hasMobileApplyClickListener = true;
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const normalizedParams = normalizeSearchParams(searchParams);
    if (normalizedParams === FacetFiltersForm.searchParamsPrev) return;

    const prevParams = FacetFiltersForm.searchParamsPrev;
    trackSearchFilterChanges(prevParams, normalizedParams);
    FacetFiltersForm.searchParamsPrev = normalizedParams;

    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    document.querySelectorAll('.thb-filter-count').forEach((item) => item.classList.add('loading'));

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) {
      FacetFiltersForm.clearLoading();
      return;
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${normalizedParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(normalizedParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }].slice(-MAX_FILTER_CACHE_ENTRIES);
        finishSectionRender(html, event);
      })
      .catch(() => FacetFiltersForm.clearLoading());
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached?.html) {
      FacetFiltersForm.clearLoading();
      return;
    }
    finishSectionRender(cached.html, event);
  }

  static clearLoading() {
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
    document.querySelectorAll('.thb-filter-count').forEach((item) => item.classList.remove('loading'));
  }

  static resizeProductCardSliders(root) {
    root.querySelectorAll('.product-card-slider.flickity-enabled').forEach((slider) => {
      const flkty = Flickity.data(slider);
      if (flkty) flkty.resize();
    });
  }

  static renderProductGridContainer(html, event) {
    const productGrid = document.getElementById('ProductGridContainer');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsed.getElementById('ProductGridContainer');
    if (!productGrid || !sourceContainer) {
      FacetFiltersForm.clearLoading();
      return;
    }

    if (isSearchPage()) {
      document.body.classList.toggle('search-no-results', Boolean(sourceContainer.querySelector('.collection-empty')));
    }

    productGrid.innerHTML = sourceContainer.innerHTML;
    productGrid.querySelector('.collection')?.classList.remove('loading');

    if (window.lazySizes?.loader) lazySizes.loader.checkElems();
    window.bassike?.rescanWishlistCollections();

    requestAnimationFrame(() => {
      FacetFiltersForm.resizeProductCardSliders(productGrid);
      requestAnimationFrame(() => FacetFiltersForm.resizeProductCardSliders(productGrid));

      const activeView = PLP_BODY_VIEWS.find((view) => document.body.classList.contains(view));
      if (activeView) ViewToggler.setActiveView(activeView);

      window.dispatchEvent(new Event('resize'));
      applyClientSidePriceFilter();
    });

    if (isPriceFacetEvent(event)) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const top = productGrid.getBoundingClientRect().top + document.documentElement.scrollTop - headerHeight - 30;
    if (top > document.documentElement.scrollTop) {
      window.scrollTo({ top, left: 0, behavior: 'smooth' });
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceLabel = parsedHTML.querySelector('.thb-filter-count .facets__label');
    const mobileSourceCount = parsedHTML.querySelector('.mobile-filter-count .facets__label')?.dataset.count;
    const renderedProductsCount = parsedHTML.querySelectorAll('#product-grid li.column').length;

    document.querySelectorAll('.thb-filter-count').forEach((item) => {
      if (sourceLabel) {
        const label = item.querySelector('.facets__label');
        if (label) label.outerHTML = sourceLabel.outerHTML;
      }
      item.classList.remove('loading');
    });

    const sourceSearchLabel = parsedHTML.querySelector('.search-label p');
    const searchLabel = document.querySelector('.search-label p');
    if (sourceSearchLabel && searchLabel) searchLabel.textContent = sourceSearchLabel.textContent;

    if (isSearchPage()) {
      const searchCount = mobileSourceCount ?? sourceLabel?.dataset.count ?? String(renderedProductsCount);
      updateResultsCount(searchCount);
      return;
    }

    const count = sourceLabel?.dataset.count ?? '';
    updateResultsCount(count);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const activeRow = event?.target?.closest('collapsible-row');
    const matchesIndex = (element) => activeRow ? element.dataset.index === activeRow.dataset.index : false;
    const facetsToRender = isSearchPage()
      ? Array.from(facetDetailsElements)
      : Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const index = element.dataset.index;
      document.querySelectorAll(`#FacetFiltersForm collapsible-row[data-index="${index}"], #FacetFiltersFormMobile collapsible-row[data-index="${index}"]`).forEach((row) => {
        row.innerHTML = element.innerHTML;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (!isSearchPage() && countsToRender && activeRow) FacetFiltersForm.renderCounts(countsToRender, activeRow);
  }

  static renderActiveFacets(html) {
    activeFacetsSelectors.forEach((selector) => {
      const sourceFacets = html.querySelector(selector);
      const targetFacets = document.querySelector(selector);
      if (!sourceFacets || !targetFacets) return;
      targetFacets.innerHTML = sourceFacets.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const source = html.querySelector('.mobile-filter-count');
    const target = document.querySelector('.mobile-filter-count');
    if (source && target) target.innerHTML = source.innerHTML;

    const count = source?.querySelector('.facets__label')?.dataset.count;
    if (count !== undefined) {
      const btn = document.querySelector('.mobile-filters-apply');
      if (btn) {
        const labelElement = btn.querySelector('span') || btn;
        if (!labelElement.dataset.countTemplate) {
          const hasNumber = /\d[\d,.]*/.test(labelElement.textContent);
          labelElement.dataset.countTemplate = hasNumber
            ? labelElement.textContent.replace(/\d[\d,.]*/, '__COUNT__')
            : `${labelElement.textContent} __COUNT__`;
        }
        labelElement.textContent = labelElement.dataset.countTemplate.replace('__COUNT__', count);
      }
      if (isSearchPage()) updateResultsCount(String(count));
    }
  }

  static renderCounts(source, target) {
    const sourceContent = source.querySelector('.thb-filter-content');
    const targetContent = target.querySelector('.thb-filter-content');
    if (sourceContent && targetContent) targetContent.innerHTML = sourceContent.innerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    const sectionId = productGrid?.dataset?.id;
    return sectionId ? [{ section: sectionId }] : [];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target?.closest('form') || this.querySelector('form');
    const formData = new FormData(form);
    const formParams = new URLSearchParams(formData);
    const params = isSearchPage()
      ? new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1))
      : new URLSearchParams(formParams);

    if (isSearchPage()) {
      const controlledNames = new Set();

      form.querySelectorAll('[name]').forEach((field) => {
        if (!field.name) return;
        controlledNames.add(field.name);
      });

      controlledNames.forEach((name) => {
        params.delete(name);
      });

      for (const [name, value] of formParams.entries()) {
        params.append(name, value);
      }
    }

    const priceSlider = form?.querySelector('.price_slider');

    if (priceSlider) {
      const rangeMax = parsePrice(priceSlider.dataset.max);
      const rangeMin = parsePrice(priceSlider.dataset.min) ?? 0;
      const minValue = parsePrice(params.get('filter.v.price.gte'));
      const maxValue = parsePrice(params.get('filter.v.price.lte'));

      // noUiSlider step:10 snaps handles to multiples of 10. Compute the snapped
      // boundary so that "handle at range edge" correctly deletes the param.
      const sliderStep = 10;
      const snappedRangeMin = rangeMin > 0 ? Math.ceil(rangeMin / sliderStep) * sliderStep : rangeMin;
      const snappedRangeMax = rangeMax != null ? Math.floor(rangeMax / sliderStep) * sliderStep : null;

      if (minValue === null || minValue <= snappedRangeMin) {
        params.delete('filter.v.price.gte');
      } else {
        params.set('filter.v.price.gte', minValue.toFixed(2));
      }

      if (maxValue === null || (snappedRangeMax != null && maxValue >= snappedRangeMax)) {
        params.delete('filter.v.price.lte');
      } else {
        params.set('filter.v.price.lte', maxValue.toFixed(2));
      }
    } else if (params.get('filter.v.price.gte') === '0.00' || params.get('filter.v.price.gte') === '0,00') {
      params.delete('filter.v.price.gte');
    }

    const nextParams = normalizeSearchTaxonomyParams(params);
    FacetFiltersForm.renderPage(nextParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const query = new URL(event.currentTarget.href).search.slice(1);
    const params = normalizeSearchTaxonomyParams(new URLSearchParams(query));
    FacetFiltersForm.renderPage(normalizeSearchParams(params.toString()));
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = normalizeSearchParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
FacetFiltersForm.hasMobileApplyClickListener = false;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}
customElements.define('facet-remove', FacetRemove);

class PriceSlider extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector('.price_slider');
    const amounts = this.querySelector('.price_slider_amount');
    const usesCommaDecimals = this.dataset.usesCommaDecimals === 'true';
    const { min, max } = getPriceParams();
    let rangeMax = parsePrice(slider.dataset.max) ?? 0;
    let rangeMin = parsePrice(slider.dataset.min) ?? 0;

    const storedRange = getStoredPriceRange();
    if (storedRange) {
      rangeMin = storedRange.min;
      rangeMax = storedRange.max;
      slider.dataset.min = String(rangeMin);
      slider.dataset.max = String(rangeMax);
    }

    const startMin = parsePrice(min) ?? parsePrice(slider.dataset.minValue) ?? rangeMin;
    const startMax = parsePrice(max) ?? parsePrice(slider.dataset.maxValue) ?? rangeMax;

    if (slider.noUiSlider) slider.noUiSlider.destroy();

    noUiSlider.create(slider, {
      start: [startMin, startMax],
      connect: true,
      step: 10,
      direction: document.dir,
      range: { min: rangeMin, max: rangeMax }
    });

    slider.noUiSlider.on('update', (values) => {
      amounts.querySelector('.field__input_min').value = formatPriceInput(values[0], usesCommaDecimals);
      amounts.querySelector('.field__input_max').value = formatPriceInput(values[1], usesCommaDecimals);
    });

    slider.noUiSlider.on('change', () => {
      const minInput = amounts.querySelector('.field__input_min');
      const maxInput = amounts.querySelector('.field__input_max');
      applyClientSidePriceFilter(parsePrice(minInput?.value), parsePrice(maxInput?.value));
      minInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });

    if (storedRange) return;

    fetchBasePriceRange().then((baseRange) => {
      if (!baseRange || !slider.noUiSlider) return;

      const nextMin = baseRange.min;
      const nextMax = baseRange.max;
      if (nextMin == null || nextMax == null) return;
      if (nextMin === rangeMin && nextMax === rangeMax) {
        setStoredPriceRange(rangeMin, rangeMax);
        return;
      }

      const prevRangeMin = rangeMin;
      const prevRangeMax = rangeMax;
      rangeMin = nextMin;
      rangeMax = nextMax;
      slider.dataset.min = String(rangeMin);
      slider.dataset.max = String(rangeMax);
      setStoredPriceRange(rangeMin, rangeMax);

      slider.noUiSlider.updateOptions({
        range: { min: rangeMin, max: rangeMax }
      });

      const currentMin = parsePrice(amounts.querySelector('.field__input_min')?.value);
      const currentMax = parsePrice(amounts.querySelector('.field__input_max')?.value);
      // A handle sitting on the old (page-derived) range edge with no price param
      // in the URL is a default, not a user choice — snap it to the real edge.
      const minIsDefault = currentMin == null || (parsePrice(min) == null && currentMin === prevRangeMin);
      const maxIsDefault = currentMax == null || (parsePrice(max) == null && currentMax === prevRangeMax);
      const nextStartMin = minIsDefault ? rangeMin : Math.max(rangeMin, Math.min(currentMin, rangeMax));
      const nextStartMax = maxIsDefault ? rangeMax : Math.min(rangeMax, Math.max(currentMax, rangeMin));
      slider.noUiSlider.set([nextStartMin, nextStartMax]);
    });
  }
}
customElements.define('price-slider', PriceSlider);

let _filterRafPending = false;
const scheduleFilterApply = () => {
  if (_filterRafPending) return;
  _filterRafPending = true;
  requestAnimationFrame(() => {
    _filterRafPending = false;
    applyClientSidePriceFilter();
  });
};

const setupPriceFilterObserver = () => {
  const productGrid = document.getElementById('ProductGridContainer');
  if (!productGrid) return;
  // Re-apply price filter whenever product cards are added/modified.
  // childList-only avoids infinite loops since our own classList.toggle() is an attribute mutation, not childList.
  new MutationObserver(scheduleFilterApply).observe(productGrid, { childList: true, subtree: true });
};

window.addEventListener('load', () => {
  new FacetsToggle();
  applyClientSidePriceFilter();
  setupPriceFilterObserver();
  document.addEventListener('pagination:page-changed', () => applyClientSidePriceFilter());
});

class ViewToggler extends HTMLElement {
  connectedCallback() {
    this.toggleButtons = this.querySelectorAll('.plp-view-toggler__button');
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        ViewToggler.setActiveView(button.dataset.view);
        try {
          window.sessionStorage.setItem(ViewToggler.getStorageKey(), button.dataset.view);
        } catch (error) {
          console.warn('[ViewToggler] Failed to save view to sessionStorage; view will not survive pagination reloads:', error);
        }
      });
    });

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      ViewToggler.clearStoredView();
      ViewToggler.setActiveView(this.toggleButtons[0]?.dataset.view);
    });

    let storedView = null;
    if (ViewToggler.isBackForwardNavigation() || !ViewToggler.isSamePathNavigation()) {
      ViewToggler.clearStoredView();
    } else {
      try {
        storedView = window.sessionStorage.getItem(ViewToggler.getStorageKey());
      } catch (error) {
        console.warn('[ViewToggler] Failed to read view from sessionStorage; falling back to default view:', error);
        storedView = null;
      }
    }
    if (storedView && ![...this.toggleButtons].some((button) => button.dataset.view === storedView)) {
      storedView = null;
    }

    ViewToggler.setActiveView(storedView || this.toggleButtons[0]?.dataset.view);
  }

  static isBackForwardNavigation() {
    const entry = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (entry) return entry.type === 'back_forward';
    return window.performance?.navigation?.type === 2;
  }

  static isSamePathNavigation() {
    if (!document.referrer) return false;
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin && referrer.pathname === window.location.pathname;
    } catch (error) {
      return false;
    }
  }

  static getStorageKey() {
    const searchTerm = (new URLSearchParams(window.location.search).get('q') || '').toLowerCase();
    return `plp-view:${window.location.pathname}:${searchTerm}`;
  }

  static clearStoredView() {
    try {
      window.sessionStorage.removeItem(ViewToggler.getStorageKey());
    } catch (error) {
      console.warn('[ViewToggler] Failed to remove view from sessionStorage:', error);
    }
  }

  static setActiveView(view) {
    if (!view) return;

    PLP_BODY_VIEWS.forEach((bodyView) => document.body.classList.remove(bodyView));
    document.body.classList.add(view);

    document.querySelectorAll('.plp-view-toggler__button').forEach((button) => {
      button.classList.toggle('active', button.dataset.view === view);
    });

    window.dispatchEvent(new Event('resize'));
  }
}

if (!customElements.get('view-toggler')) {
  customElements.define('view-toggler', ViewToggler);
}
